// client/src/pages/adminpages/PointBankChargeCheckPage.js
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import '../../styles/admin/AdminSummary.css';
import '../../styles/admin/AdminLayout.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function fmt(d) {
  if (!d) return '-';
  try { return new Date(d).toLocaleString('ko-KR'); } catch { return String(d); }
}

function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d).toISOString().slice(0, 10); } catch { return ''; }
}

function getThisMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { startDate: fmtDate(start), endDate: fmtDate(end) };
}

// 공통 확인 모달
function ConfirmModal({ open, title, children, onConfirm, onCancel, loading, confirmLabel = '확인', confirmStyle }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <div style={{ background: 'var(--admin-bg-panel)', padding: 24, borderRadius: 'var(--admin-card-radius)', width: 480, maxWidth: '92%', boxShadow: 'var(--admin-shadow-xl)' }}>
        <h3 style={{ margin: '0 0 12px 0', color: 'var(--admin-text-main)' }}>{title}</h3>
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, color: 'var(--admin-text-body)', lineHeight: 1.7 }}>{children}</div>
        <div className="admin-section__actions" style={{ marginTop: 20, justifyContent: 'flex-end' }}>
          <button className="admin-section__button admin-section__button--secondary" onClick={onCancel} disabled={loading}>취소</button>
          <button
            className="admin-section__button"
            onClick={onConfirm}
            disabled={loading}
            style={confirmStyle}
          >
            {loading ? '처리 중...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 탭 1: 만료 / 취소 요청 ────────────────────────────────────────────────
function ExpiredTab() {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [resolveTarget, setResolveTarget] = useState(null); // { merchantUid, status, userId, userName, points, amount, bankDepositor }
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/point/bank-charge/expired`, { credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) throw new Error('목록을 불러오지 못했습니다.');
      setItems(j.items || []);
    } catch (e) { setError(e.message || '서버 오류'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleResolve = async () => {
    if (!resolveTarget) return;
    setActionLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/point/bank-charge/expired/${encodeURIComponent(resolveTarget.merchantUid)}/resolve`,
        { method: 'POST', credentials: 'include' }
      );
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) { alert(j?.message || '처리 실패'); }
      else { setResolveTarget(null); load(); }
    } catch { alert('서버 오류'); }
    setActionLoading(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/point/bank-charge/expired/${encodeURIComponent(deleteTarget.merchantUid)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) { alert(j?.message || '삭제 실패'); }
      else { setDeleteTarget(null); load(); }
    } catch { alert('서버 오류'); }
    setActionLoading(false);
  };

  const StatusBadge = ({ status }) => (
    status === 'expired'
      ? <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, background: '#fff3e0', color: '#e65100', fontSize: 12, fontWeight: 600 }}>🟠 만료</span>
      : <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, background: '#f5f5f5', color: '#616161', fontSize: 12, fontWeight: 600 }}>⚫ 취소</span>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 14, color: 'var(--admin-text-sub)' }}>
          전체 <strong>{items.length}</strong>건
          &nbsp;·&nbsp;🟠 만료 <strong>{items.filter(i => i.status === 'expired').length}</strong>건
          &nbsp;·&nbsp;⚫ 취소 <strong>{items.filter(i => i.status === 'cancel').length}</strong>건
        </div>
        <button className="admin-section__button admin-section__button--secondary" style={{ fontSize: 13 }} onClick={load}>새로고침</button>
      </div>

      {error && <div style={{ color: 'var(--admin-color-danger)', marginBottom: 12, fontSize: 14 }}>{error}</div>}
      {loading && <div style={{ color: 'var(--admin-text-sub)', fontSize: 14 }}>불러오는 중...</div>}

      {!loading && items.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--admin-text-sub)', fontSize: 14 }}>
          만료 / 취소 요청이 없습니다.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--admin-bg-hover)', borderBottom: '2px solid var(--admin-border-color)' }}>
                {['요청일시', '만료·취소일시', '광고주 (ID/이름)', '포인트', '입금금액', '입금자명', '구분', '액션'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--admin-text-main)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.merchantUid} style={{ borderBottom: '1px solid var(--admin-border-color)' }}>
                  <td style={{ padding: '10px 12px', color: 'var(--admin-text-body)', whiteSpace: 'nowrap' }}>{fmt(it.createdAt)}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--admin-text-body)', whiteSpace: 'nowrap' }}>{fmt(it.updatedAt)}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--admin-text-body)' }}>
                    <div style={{ fontWeight: 500 }}>{it.userId}</div>
                    <div style={{ fontSize: 12, color: 'var(--admin-text-sub)' }}>{it.userName}</div>
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--admin-text-body)', whiteSpace: 'nowrap' }}>{Number(it.points).toLocaleString()} P</td>
                  <td style={{ padding: '10px 12px', color: 'var(--admin-text-body)', whiteSpace: 'nowrap' }}>{Number(it.amount).toLocaleString()} 원</td>
                  <td style={{ padding: '10px 12px', color: 'var(--admin-text-body)' }}>{it.bankDepositor || '-'}</td>
                  <td style={{ padding: '10px 12px' }}><StatusBadge status={it.status} /></td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                    <button
                      className="admin-section__button"
                      style={{ fontSize: 12, padding: '4px 10px', marginRight: 6, backgroundColor: 'var(--admin-color-success)', borderColor: 'var(--admin-color-success)' }}
                      onClick={() => setResolveTarget(it)}
                    >
                      수동 지급
                    </button>
                    <button
                      className="admin-section__button admin-section__button--secondary"
                      style={{ fontSize: 12, padding: '4px 10px', color: 'var(--admin-color-danger)', borderColor: 'var(--admin-color-danger)' }}
                      onClick={() => setDeleteTarget(it)}
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 수동 지급 확인 모달 */}
      <ConfirmModal
        open={!!resolveTarget}
        title="포인트 수동 지급"
        loading={actionLoading}
        confirmLabel="지급 확정"
        confirmStyle={{ backgroundColor: 'var(--admin-color-success)', borderColor: 'var(--admin-color-success)' }}
        onConfirm={handleResolve}
        onCancel={() => setResolveTarget(null)}
      >
        {resolveTarget && (
          <>
            {resolveTarget.status === 'cancel'
              ? '⚠️ 광고주가 직접 취소한 요청입니다. 실제 입금을 확인하셨습니까?\n\n'
              : '실제 입금을 확인하셨습니까?\n\n'}
            광고주: {resolveTarget.userId} ({resolveTarget.userName}){'\n'}
            포인트: {Number(resolveTarget.points).toLocaleString()} P{'\n'}
            입금금액: {Number(resolveTarget.amount).toLocaleString()} 원{'\n'}
            입금자명: {resolveTarget.bankDepositor || '-'}
          </>
        )}
      </ConfirmModal>

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        open={!!deleteTarget}
        title="건 삭제"
        loading={actionLoading}
        confirmLabel="삭제"
        confirmStyle={{ backgroundColor: 'var(--admin-color-danger)', borderColor: 'var(--admin-color-danger)' }}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      >
        {deleteTarget && (
          <>
            ⚠️ 해당 건에 실제 입금이 없음을 확인했습니까?{'\n'}
            삭제 후 복구할 수 없습니다.{'\n\n'}
            광고주: {deleteTarget.userId} ({deleteTarget.userName}){'\n'}
            포인트: {Number(deleteTarget.points).toLocaleString()} P{'\n'}
            입금자명: {deleteTarget.bankDepositor || '-'}
          </>
        )}
      </ConfirmModal>
    </div>
  );
}

// ─── 탭 2: 충전 완료 내역 ────────────────────────────────────────────────────
function CompletedTab() {
  const { startDate: initStart, endDate: initEnd } = getThisMonthRange();
  const [startDate, setStartDate] = useState(initStart);
  const [endDate, setEndDate]     = useState(initEnd);
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [searched, setSearched]   = useState(false);

  const load = useCallback(async (sd, ed) => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (sd) params.set('startDate', sd);
      if (ed) params.set('endDate', ed);
      const r = await fetch(`${API_URL}/api/admin/point/bank-charge/completed?${params}`, { credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) throw new Error('목록을 불러오지 못했습니다.');
      setItems(j.items || []);
      setSearched(true);
    } catch (e) { setError(e.message || '서버 오류'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(initStart, initEnd); }, [load, initStart, initEnd]);

  const totalAmount = items.reduce((sum, i) => sum + (i.amount || 0), 0);

  const MethodBadge = ({ resolvedManually }) => (
    resolvedManually
      ? <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, background: '#fff3e0', color: '#e65100', fontSize: 12, fontWeight: 600 }}>수동</span>
      : <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, background: '#e8f5e9', color: '#2e7d32', fontSize: 12, fontWeight: 600 }}>자동</span>
  );

  return (
    <div>
      {/* 기간 필터 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid var(--admin-border-color)', borderRadius: 'var(--admin-input-radius)', fontSize: 13, background: 'var(--admin-bg-input)', color: 'var(--admin-text-main)' }}
        />
        <span style={{ color: 'var(--admin-text-sub)', fontSize: 13 }}>~</span>
        <input
          type="date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid var(--admin-border-color)', borderRadius: 'var(--admin-input-radius)', fontSize: 13, background: 'var(--admin-bg-input)', color: 'var(--admin-text-main)' }}
        />
        <button className="admin-section__button" style={{ fontSize: 13, padding: '6px 16px' }} onClick={() => load(startDate, endDate)}>
          조회
        </button>
        <button
          className="admin-section__button admin-section__button--secondary"
          style={{ fontSize: 13, padding: '6px 14px' }}
          onClick={() => { setStartDate(initStart); setEndDate(initEnd); load(initStart, initEnd); }}
        >
          이번 달
        </button>
      </div>

      {error && <div style={{ color: 'var(--admin-color-danger)', marginBottom: 12, fontSize: 14 }}>{error}</div>}
      {loading && <div style={{ color: 'var(--admin-text-sub)', fontSize: 14 }}>불러오는 중...</div>}

      {!loading && searched && (
        <div style={{ fontSize: 14, color: 'var(--admin-text-sub)', marginBottom: 12 }}>
          {items.length}건 &nbsp;·&nbsp; 합계 <strong style={{ color: 'var(--admin-text-main)' }}>{totalAmount.toLocaleString()}</strong>원
        </div>
      )}

      {!loading && searched && items.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--admin-text-sub)', fontSize: 14 }}>
          해당 기간에 충전 완료 내역이 없습니다.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--admin-bg-hover)', borderBottom: '2px solid var(--admin-border-color)' }}>
                {['처리일시', '광고주 (ID/이름)', '포인트', '금액', '처리방식'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--admin-text-main)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.merchantUid} style={{ borderBottom: '1px solid var(--admin-border-color)' }}>
                  <td style={{ padding: '10px 12px', color: 'var(--admin-text-body)', whiteSpace: 'nowrap' }}>{fmt(it.paidAt)}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--admin-text-body)' }}>
                    <div style={{ fontWeight: 500 }}>{it.userId}</div>
                    <div style={{ fontSize: 12, color: 'var(--admin-text-sub)' }}>{it.userName}</div>
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--admin-text-body)', whiteSpace: 'nowrap' }}>{Number(it.points).toLocaleString()} P</td>
                  <td style={{ padding: '10px 12px', color: 'var(--admin-text-body)', whiteSpace: 'nowrap' }}>{Number(it.amount).toLocaleString()} 원</td>
                  <td style={{ padding: '10px 12px' }}><MethodBadge resolvedManually={it.resolvedManually} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── 메인 페이지 ─────────────────────────────────────────────────────────────
export default function PointBankChargeCheckPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const initialTab = params.get('tab') === 'completed' ? 'completed' : 'expired';
  const [activeTab, setActiveTab] = useState(initialTab);

  const tabStyle = (tab) => ({
    padding: '10px 24px',
    border: 'none',
    borderBottom: activeTab === tab ? '2px solid var(--admin-color-primary)' : '2px solid transparent',
    background: 'transparent',
    color: activeTab === tab ? 'var(--admin-color-primary)' : 'var(--admin-text-sub)',
    fontWeight: activeTab === tab ? 700 : 400,
    fontSize: 15,
    cursor: 'pointer',
    transition: 'all 0.15s',
  });

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/admin/point/bank-charge/check?tab=${tab}`, { replace: true });
  };

  return (
    <AdminLayout title="포인트 충전 관리">
      {/* 탭 네비게이션 */}
      <div style={{ borderBottom: '1px solid var(--admin-border-color)', marginBottom: 24, marginTop: 8 }}>
        <button style={tabStyle('expired')} onClick={() => handleTabChange('expired')}>
          ⏰ 만료 / 취소 요청
        </button>
        <button style={tabStyle('completed')} onClick={() => handleTabChange('completed')}>
          ✅ 충전 완료 내역
        </button>
      </div>

      {/* 탭 콘텐츠 */}
      <div style={{ background: 'var(--admin-bg-panel)', borderRadius: 'var(--admin-card-radius)', border: '1px solid var(--admin-border-color)', padding: 20, boxShadow: 'var(--admin-shadow-sm)' }}>
        {activeTab === 'expired'   && <ExpiredTab />}
        {activeTab === 'completed' && <CompletedTab />}
      </div>
    </AdminLayout>
  );
}
